// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { EmptyState } from '@/components/greenlight/EmptyState';
import {
  AGENT_SETUP_PATH,
  MARKETPLACE_CREATE_PATH,
  OrgAdminContacts,
  pluginSetupState,
} from '@/components/settings/pluginSetup';
import { Button } from '@/components/ui/button';
import type { OrgRole, OrgSettings } from '@greenlight/shared';

export interface AppsEmptyStateProps {
  role: OrgRole | undefined;
  /** Undefined when settings could not be read; the builder path still leads to Agent setup. */
  agentSetup: OrgSettings['agent_setup'] | undefined;
}

async function copyAgentSetupLink(): Promise<void> {
  try {
    await navigator.clipboard.writeText(new URL(AGENT_SETUP_PATH, window.location.origin).href);
    toast.success('Agent setup link copied.');
  } catch {
    toast.error("Couldn't copy the link.");
  }
}

export function AppsEmptyState({ role, agentSetup }: AppsEmptyStateProps): React.JSX.Element {
  const hasMarketplace = agentSetup ? agentSetup.marketplace_repositories.length > 0 : true;
  const state = pluginSetupState(role, hasMarketplace);

  switch (state) {
    case 'admin-rollout':
      return (
        <EmptyState
          title="Roll out the Greenlight plugin to your org"
          description="Builders' coding agents install the Greenlight plugin from a marketplace repository your org owns. Create one, then share the install steps with your builders; every app they build shows up here."
          action={
            <Button asChild size="sm">
              <Link to={MARKETPLACE_CREATE_PATH}>Create a marketplace repository</Link>
            </Button>
          }
        />
      );
    case 'admin-ready':
      return (
        <EmptyState
          title="Your org has the plugin"
          description="Install it in your own coding agent and describe an app to build one, or send your builders the Agent setup page. Every app they deploy shows up here."
          action={
            <div className="flex flex-wrap justify-center gap-2">
              <Button asChild size="sm">
                <Link to={AGENT_SETUP_PATH}>Install the plugin</Link>
              </Button>
              <Button size="sm" variant="outline" onClick={() => void copyAgentSetupLink()}>
                Copy setup link for builders
              </Button>
            </div>
          }
        />
      );
    case 'builder-waiting':
      return (
        <EmptyState
          title="Your admin hasn't set up the Greenlight plugin yet"
          description="Your coding agent builds and deploys apps through the Greenlight plugin, and an org admin sets it up once for everyone."
          action={
            <p className="text-sm text-muted-foreground" data-testid="apps-empty-admins">
              <OrgAdminContacts admins={agentSetup?.org_admins} />
            </p>
          }
        />
      );
    case 'builder-ready':
      return (
        <EmptyState
          title="Build your first app"
          description="Install the Greenlight plugin in your coding agent, then describe the app you want. Your agent builds and deploys it, and it shows up here."
          action={
            <Button asChild size="sm">
              <Link to={AGENT_SETUP_PATH}>Install the plugin</Link>
            </Button>
          }
        />
      );
  }
}
